/**
 * Theme Selector - Allows users to customize chat colors
 */

import React, { useState } from 'react';
import { Modal, Form, ColorPicker, Button, Space, Typography, Divider } from 'antd';
import { BgColorsOutlined } from '@ant-design/icons';
import { useChatTheme } from '../../contexts/ChatThemeContext';
import './ThemeSelector.css';

const { Text } = Typography;

interface ThemeSelectorProps {
  trigger?: React.ReactNode;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ 
  trigger 
}) => {
  const { theme, setTheme, resetTheme } = useChatTheme();
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();

  const handleOpen = () => {
    form.setFieldsValue(theme);
    setVisible(true);
  };

  const handleSave = (values: any) => {
    setTheme(values);
    setVisible(false);
  };

  const handleReset = () => {
    resetTheme();
    form.setFieldsValue(defaultTheme);
  };

  const defaultTheme = {
    sidebarBg: '#350d36',
    sidebarText: '#ffffff',
    sidebarHover: '#3f1040',
    sidebarActive: '#1164a3',
    messageBg: '#ffffff',
    messageText: '#1d1c1d',
    inputBg: '#ffffff',
    inputBorder: '#e8e8e8',
    sendButton: '#007a5a',
    sendButtonHover: '#148567',
  };

  return (
    <>
      {trigger ? (
        <div onClick={handleOpen}>{trigger}</div>
      ) : (
        <Button 
          type="text" 
          icon={<BgColorsOutlined />} 
          onClick={handleOpen}
          style={{ color: 'var(--chat-sidebar-text, #2f343d)' }}
        >
          Theme
        </Button>
      )}

      <Modal
        title="Customize Chat Theme"
        open={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          onFinish={handleSave}
          layout="vertical"
          initialValues={theme}
        >
          <div className="theme-section">
            <Text strong style={{ fontSize: 14, marginBottom: 12, display: 'block' }}>
              Sidebar Colors
            </Text>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Form.Item name="sidebarBg" label="Background">
                <ColorPicker showText format="hex" />
              </Form.Item>
              <Form.Item name="sidebarText" label="Text">
                <ColorPicker showText format="hex" />
              </Form.Item>
              <Form.Item name="sidebarHover" label="Hover">
                <ColorPicker showText format="hex" />
              </Form.Item>
              <Form.Item name="sidebarActive" label="Active Channel">
                <ColorPicker showText format="hex" />
              </Form.Item>
            </Space>
          </div>

          <Divider />

          <div className="theme-section">
            <Text strong style={{ fontSize: 14, marginBottom: 12, display: 'block' }}>
              Message Area
            </Text>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Form.Item name="messageBg" label="Background">
                <ColorPicker showText format="hex" />
              </Form.Item>
              <Form.Item name="messageText" label="Text">
                <ColorPicker showText format="hex" />
              </Form.Item>
            </Space>
          </div>

          <Divider />

          <div className="theme-section">
            <Text strong style={{ fontSize: 14, marginBottom: 12, display: 'block' }}>
              Input Area
            </Text>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Form.Item name="inputBg" label="Background">
                <ColorPicker showText format="hex" />
              </Form.Item>
              <Form.Item name="inputBorder" label="Border">
                <ColorPicker showText format="hex" />
              </Form.Item>
              <Form.Item name="sendButton" label="Send Button">
                <ColorPicker showText format="hex" />
              </Form.Item>
              <Form.Item name="sendButtonHover" label="Send Button Hover">
                <ColorPicker showText format="hex" />
              </Form.Item>
            </Space>
          </div>

          <Divider />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Apply Theme
              </Button>
              <Button onClick={handleReset}>
                Reset to Default
              </Button>
              <Button onClick={() => setVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

